import jQuery from 'jquery';
import {errorHandler} from "../misc/errors";

// ---------------------------
// Monkey patch Onsen

type LoaderParams = {
    page: any,
    parent: HTMLElement,
    params: any
};

// ---------------------------

export default abstract class View {

    private static id: number = 1;

    private uuid = this.genUuid();

    readonly classname?: string;

    private parent?: View;

    /**
     * Stuff that should be executed once the DOM has been updated.
     */
    private afterUpdateQueue?: Array<() => void> = undefined;

    protected constructor(classname?: string) {
        this.classname = classname;
    }

    setParent(parent: View) {
        this.parent = parent;
    }

    getParent() {
        return this.parent;
    }

    getRoot() : View {
        if (this.parent) {
            return this.parent.getRoot();
        } else {
            return this;
        }
    }

    getUuid() {
        return this.uuid;
    }

    getHtmlClass() : string | undefined {
        return undefined;
    }

    /**
     * Find the element corresponding to this view.
     */
    elt(): JQuery<HTMLElement> {
        const doc = jQuery(document);
        const elt: JQuery<HTMLElement> = doc.find("#" + this.uuid) as unknown as JQuery<HTMLElement>;
        return elt;
    }

    /**
     * Return true if this View is rendered in the DOM
     */
    rendered() : boolean {
        return this.elt().length > 0;
    }

    /**
     * Update the page with a re-rendered version of this view.
     */
    update(selector?: string | string[]) {

        try {

            this.afterUpdateQueue = this.afterUpdateQueue || [];

            const x = this.render();

            this.replaceElements(x, selector);

            for (const fn of this.afterUpdateQueue) {
                fn();
            }

            this.afterUpdateQueue = undefined;

        } catch (ex) {
            errorHandler(ex);
        }

    }

    protected replaceElements(rendered: JQuery<HTMLElement>, selector?: string | string[]) {
        const elt = this.elt();
        const replace = (sel?: string) => {
            const src = sel ? rendered.find(sel).addBack(sel) : rendered;
            const target = sel ? elt.find(sel).addBack(sel) : elt;
            target.replaceWith(src);
        };

        if (typeof selector === "undefined") {
            replace();
        } else {
            if (typeof selector === "string") {
                selector = [ selector ];
            }
            for (let sel of selector) {
                replace(sel);
            }
        }
    }

    /**
     * Render view to HTML (jQuery object). The caller will add classname and id.
     */
    protected abstract render0(): JQuery<HTMLElement>;

    render(): JQuery<HTMLElement> {
        let $out = this.render0();

        if ($out.length > 1) {
            const wrapper = jQuery("<div>");
            wrapper.append($out);
            $out = wrapper;
        }

        $out.attr("id", this.getUuid());
        if (this.classname) {
            $out.addClass(this.classname);
        }
        $out.addClass("widget");
        $out.data("view", this);

        return $out;
    }

    /**
     * This is intented to be called within "render" methods, to invoke code that
     * requires the elements to be present in the DOM.
     */
    protected afterUpdate(fn: () => void) {
        if (this.afterUpdateQueue) {
            this.afterUpdateQueue.push(fn);
            return;
        }
        if (this.parent) {
            this.parent.afterUpdate(fn);
            return;
        }
        // Whoops nowhere to put the postUpdate. We must be not in an update phase.
        // Assume we are a top-level component and create the queue.
        // Note that this won't work eg if we are a lower-level component but not
        // yet with an assigned parent.

        this.afterUpdateQueue = [ fn ];
    }

    pug(template: (locals: any) => any, locals: { [key:string] : any }) {
        // detect jQuery objects to replace

        locals = locals || {};
        const locals1: { uuid?: string, [key:string] : any } = {};
        const subs: { [id:string] : any }  = {};

        for (const i in locals) {
            const val = locals[i];

            if (val instanceof View) {
                // View, so render first

                // Side effect: parent of val is set to this if not already set.
                // This means that the render method can see the tree.
                if (! val.parent) {
                    val.setParent(this);
                }

                const id = val.getUuid();
                const str = `<div id='${id}'></div>`;
                subs[id] = val.render();
                locals1[i] = str;


            } else if (val && val.attr && val.attr('id')) {
                // JQuery object

                const id = val.attr('id');
                const str = `<div id='${id}'></div>`;
                subs[id] = val;
                locals1[i] = str;

            } else {
                // Nothing special, just use literal value

                locals1[i] = val;
            }
        }

        // Add uuid
        locals1.uuid = this.getUuid();

        // @ts-ignore
        locals1.serverUrl = window.aidka_server_url || ".";

        try {
            const html = template(locals1) || "<div></div>";
            const out = jQuery(html);

            for (var id in subs) {
                const sel = '#' + id;
                const found = out.find(sel).addBack(sel);
                found.replaceWith(subs[id]);
            }

            return out;
        } catch (ex) {

            errorHandler(ex);

            return jQuery("<div>").text("Error in " + this.classname);

        }
    }

    /**
     * Register a listener for this widget.
     * @param widgetSelector is a jQuery selector that identifies the widget (eg, the classname)
     * @param events the events to listen for, eg "click"
     * @param selector_data is a jQuery selector which is used to filter events
     * @param handler will be invoked when a matching event is received.
     */
    static on<T extends View> (widgetSelector: string,
                               events: string,
                               selector_data: any,
                               handler: ((this: T, that: T, event: JQueryEventObject, elt: HTMLElement) => void)) {

        jQuery(document).on(events, selector_data, function(event: JQueryEventObject) {

            // Find instance
            const source = jQuery(event.target);
            const widget = source.closest(widgetSelector);

            if (widget.length == 0) {
                // Not us
                return
            }

            const view = widget.data("view") as T;

            if (! view) {
                // Wrong element - maybe ambiguous selector.
                return;
            }

            const elt = this as unknown as HTMLElement;

            try {
                handler.call(view, view, event, elt);
            } catch (ex) {
                errorHandler(ex);
            }

        });

        return this;
    }

    private genUuid() {
        return "v-" + (View.id++);
    }

    notify(n: Notification) {
        if (this.parent) {
            this.parent.notify(n);
        }
    }

}



export class Notification {
    name: string;
    constructor(n: string) {
        this.name = n;
    }
}
